// Default Pixi texture factory for LetterLayer / useThemeAtlas.
//
// Extracted from LetterLayer.tsx so the cold-cache probe behavior is unit-
// testable in isolation (only `pixi.js` needs mocking — the LetterLayer module
// pulls in @pixi/react + the whole sprite graph).
//
// Resolves a Pixi Texture by atlas frame name. The cache must be primed by the
// theme-load flow (atlas-loader path); on a cold/missing cache it returns
// Texture.EMPTY so missing assets render transparent rather than throwing.

import type { Texture } from 'pixi.js'
import { Assets, Texture as PixiTexture } from 'pixi.js'

type AssetsLike = {
  get?: (key: string) => unknown
  cache?: { has?: (key: string) => boolean }
}

/**
 * Resolve a Pixi Texture by atlas frame name.
 *
 * Both lookups are guarded by `Assets.cache.has(key)` FIRST: `Assets.get(key)`
 * emits a `[Assets] Asset id <key> was not found in the Cache` warning on a
 * miss, and on first paint the atlas warm-up has not resolved yet (the cache is
 * cold), so probing unconditionally floods the console with one warning per
 * cube/frame. `has()` does not warn, so we only call `get()` when the key is
 * actually present. Missing/cold → Texture.EMPTY (transparent).
 */
export function defaultTextureFactory(frameName: string): unknown {
  const assets = Assets as unknown as AssetsLike
  const cache = assets.cache

  // Primary: the loaded atlas spritesheet exposes frames under `.textures`.
  if (cache?.has?.('atlas')) {
    const atlas = assets.get?.('atlas') as
      | { textures?: Record<string, Texture> }
      | undefined
    const tx = atlas?.textures?.[frameName]
    if (tx) return tx
  }

  // Fallback: Pixi v8 also registers each spritesheet frame under its bare key
  // (cachePrefix='') on a WARM cache, so a direct lookup resolves a real
  // Texture once loaded. Guarded by has() for the same no-cold-warning reason —
  // and KEPT (not dropped): it is a live path on a warm cache.
  if (cache?.has?.(frameName)) {
    const tx = assets.get?.(frameName) as Texture | undefined
    if (tx) return tx
  }

  // Final fallback: the canonical empty texture. Tests that wish to avoid the
  // Pixi import should inject a custom `textureFactory` prop instead.
  return PixiTexture.EMPTY
}
